/**
 * Fired after a tool call (in-app assistant or WebMCP) changed data on the server, so open pages
 * can reload what they show. `projectId` is absent when the change was not about one scene.
 */
export const LIGHTSCAPE_CHANGED_EVENT = 'lightscape:changed'

export type LightscapeChangedDetail = { projectId?: string; tool?: string }

export const dispatchLightscapeChanged = (detail: LightscapeChangedDetail) => {
  window.dispatchEvent(new CustomEvent<LightscapeChangedDetail>(LIGHTSCAPE_CHANGED_EVENT, { detail }))
}

/**
 * Tools that change a scene or the scene list. Their successful results trigger a live reload.
 * (`lightscape_ai_generate_image` writes too, but only to the asset library — no scene changes.)
 */
export const WRITE_TOOLS = new Set([
  'lightscape_project_create',
  'lightscape_project_rename',
  'lightscape_project_delete',
  'lightscape_version_restore',
  'lightscape_scene_patch',
  'lightscape_scene_undo',
  'lightscape_scene_add_image',
  'lightscape_publish',
  'lightscape_unpublish',
])

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

/** A tool output is a failure when the server wrapped it as `{ error }`. */
export const isToolFailure = (output: unknown): boolean => 'error' in asRecord(output)

/** The scene a tool call touched: its `projectId` argument, or the id a create returned. */
export const projectIdOfCall = (name: string, input: unknown, output: unknown) => {
  const fromInput = asRecord(input).projectId
  if (typeof fromInput === 'string') return fromInput
  const fromOutput = asRecord(output).id
  return name === 'lightscape_project_create' && typeof fromOutput === 'string'
    ? fromOutput
    : undefined
}
